import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import CustomButton from "../../components/OnboardingComponents/CustomButton"
import { Avatar } from "../../components/DashboardComponents/AvatarGroup"
import EmptyState from "../../components/Common/EmptyState"
import MediaFallbackImage from "../../components/Common/MediaFallbackImage"
import { ICONS } from "../../constants/icons"
import { get } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { getUserAvatarUrl } from "../../helpers/userAvatar"
import { isSameUserId, nameWithYou } from "../../helpers/userDisplay"
import {
  fetchCitySpellbookSummary,
  genomeConicGradient,
} from "../Profile/profileGenome"
import { ExploreCitiesModal } from "./CityHubModals"
import photoPlayIcon from "../../assets/images/city-hub/photo-play.svg"
import {
  getUserCityHubLevel,
  pickDefaultCityHubCity,
  resolveCityHubCityForLevel,
} from "./cityHubCity"
import { cityHubTabPath } from "./cityHubTabs"
import {
  RECENT_PHOTOS_WIDGET_LIMIT,
  fetchRecentCityPhotos,
} from "./cityPhotography"

const fallbackCity = pickDefaultCityHubCity(null)
const COLLABORATORS_WIDGET_LIMIT = 6
const GENOME_SKELETON_ROWS = 5
const MEMBER_ROLE_WITH = ["currentRoles.roleTypes"]

const extractMembersPage = payload => {
  const result = payload?.data?.result
  if (Array.isArray(result)) return result
  if (Array.isArray(result?.data)) return result.data
  return []
}

const formatMemberName = user =>
  [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
  user?.full_name ||
  user?.name ||
  user?.email ||
  "N/A"

const roleTypeName = role => {
  const types = role?.role_types || role?.roleTypes || []
  const type = types[0]
  return String(type?.public_name || type?.name || "").trim()
}

const pickMemberRole = user => {
  const roles = user?.current_roles || user?.currentRoles || []
  const ongoing = roles.find(role => role?.is_ongoing)
  const fromOngoing = roleTypeName(ongoing)
  if (fromOngoing) return fromOngoing

  const fromAny = roles.map(roleTypeName).find(Boolean)
  if (fromAny) return fromAny

  const fallback = ongoing || roles[0]
  const label =
    (typeof fallback?.role === "string" ? fallback.role.trim() : "") ||
    fallback?.project_name
  return String(label || "").trim() || "N/A"
}

const getXpLevel = points => {
  const xp = Number(points)
  const safe = Number.isFinite(xp) ? Math.max(0, xp) : 0
  return Math.floor(safe / 1000) + 1
}

const mapCityMember = (user, currentUserId) => {
  const xp = Number(user?.total_xp ?? user?.xp_points ?? 0) || 0
  const name = formatMemberName(user)
  return {
    id: user?.id,
    name: nameWithYou(name, isSameUserId(user?.id, currentUserId)),
    role: pickMemberRole(user),
    avatar: getUserAvatarUrl(user),
    xp,
    level: getXpLevel(xp),
  }
}

const CollaboratorSkeletons = ({ count = 6 }) =>
  Array.from({ length: count }, (_, index) => (
    <div
      key={`collab-skel-${index}`}
      className="city-hub__collaborator city-hub__collaborator--skeleton"
      aria-hidden="true"
    >
      <div className="amok-skeleton city-hub__collab-skel-avatar" />
      <div className="city-hub__collab-info">
        <div className="amok-skeleton city-hub__collab-skel-line" />
        <div className="amok-skeleton city-hub__collab-skel-line city-hub__collab-skel-line--sm" />
      </div>
    </div>
  ))

const RecentPhotoSkeletons = ({ count = RECENT_PHOTOS_WIDGET_LIMIT }) =>
  Array.from({ length: count }, (_, index) => (
    <div
      key={`photo-skel-${index}`}
      className="city-hub__photo city-hub__photo--skeleton"
      aria-hidden="true"
    >
      <div className="amok-skeleton city-hub__photo-skel" />
    </div>
  ))

export const CityHubBanner = ({
  city = fallbackCity,
  yourCities = [],
  isLoading = false,
  onSelectCity,
  canExplore = true,
  isPublic = false,
}) => {
  const [isExploreOpen, setIsExploreOpen] = useState(false)
  const currentUser = useSelector(state => state.auth.currentUser)
  const userLevel = getUserCityHubLevel(currentUser)
  const displayCity = city || fallbackCity

  const handleSelectCity = nextCity => {
    if (isPublic) {
      onSelectCity?.(nextCity)
    } else {
      onSelectCity?.(resolveCityHubCityForLevel(nextCity, userLevel))
    }
    setIsExploreOpen(false)
  }

  const openExplore = () => {
    if (!canExplore) return
    setIsExploreOpen(true)
  }

  const cityLabel = (
    <>
      <div className="city-hub__city-row">
        <h1 className="city-hub__city-name">
          {isLoading ? "Loading…" : displayCity.name}
        </h1>
        {canExplore ? (
          <img
            src={ICONS.chevronDown}
            alt=""
            className="city-hub__city-chevron"
            width={16}
            height={16}
          />
        ) : null}
        <span className="city-hub__city-badge">{displayCity.type}</span>
      </div>
      <p className="city-hub__city-tagline">{displayCity.tagline}</p>
    </>
  )

  return (
    <>
      <div className="city-hub__banner" aria-busy={isLoading || undefined}>
        {displayCity.banner ? (
          <img
            src={displayCity.banner}
            alt=""
            className="city-hub__banner-bg"
          />
        ) : (
          <div className="city-hub__banner-bg city-hub__banner-bg--placeholder" />
        )}
        <div className="city-hub__banner-overlay" aria-hidden="true" />
        <div className="city-hub__banner-content">
          {canExplore ? (
            <button
              type="button"
              className="city-hub__city city-hub__city--button"
              onClick={openExplore}
              aria-label="Explore cities"
            >
              {cityLabel}
            </button>
          ) : (
            <div className="city-hub__city">{cityLabel}</div>
          )}
          {canExplore ? (
            <div className="city-hub__banner-actions">
              <CustomButton
                variant="ghost"
                size="md"
                className="city-hub__btn-outline"
                trailingIconSrc={ICONS.navCityHub}
                trailingIconAlt=""
                onClick={openExplore}
              >
                Explore Cities
              </CustomButton>
            </div>
          ) : null}
        </div>
      </div>

      {canExplore ? (
        <ExploreCitiesModal
          isOpen={isExploreOpen}
          onClose={() => setIsExploreOpen(false)}
          activeCityId={displayCity.id || undefined}
          yourCities={isPublic ? [] : yourCities}
          userLevel={userLevel}
          onSelectCity={handleSelectCity}
          isPublic={isPublic}
        />
      ) : null}
    </>
  )
}

export const CityHubGenome = ({ city = fallbackCity }) => {
  const cityShortName = city?.shortName || fallbackCity.shortName
  const cityId = city?.id || null
  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(Boolean(cityId))

  useEffect(() => {
    let cancelled = false

    const loadSpellbook = async () => {
      if (!cityId) {
        setItems([])
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      const summary = await fetchCitySpellbookSummary(cityId)
      if (!cancelled) {
        setItems(summary)
        setIsLoading(false)
      }
    }

    loadSpellbook()
    return () => {
      cancelled = true
    }
  }, [cityId])

  const totalPercent = items.reduce(
    (sum, item) => sum + (Number(item.value) || 0),
    0,
  )

  const showSkeleton = isLoading
  const showEmpty = !isLoading && items.length === 0

  return (
    <div className="city-hub__panel city-hub__panel--genome">
      <div className="city-hub__genome-header">
        <div className="city-hub__genome-titles">
          <h3 className="city-hub__panel-title">{cityShortName} at a Glance</h3>
          <p className="city-hub__panel-subtitle">Arts Genome - Spellbook</p>
        </div>
        {showSkeleton ? (
          <span
            className="city-hub__genome-donut city-hub__genome-donut--skeleton amok-skeleton amok-skeleton--circle"
            aria-hidden="true"
          />
        ) : items.length > 0 ? (
          <span
            className="city-hub__genome-donut"
            style={{ background: genomeConicGradient(items) }}
            aria-hidden="true"
          />
        ) : null}
      </div>
      <div
        className="city-hub__genome"
        aria-busy={showSkeleton || undefined}
      >
        {showSkeleton ? (
          <>
            {Array.from({ length: GENOME_SKELETON_ROWS }).map((_, index) => (
              <div
                key={`genome-skel-${index}`}
                className="city-hub__genome-row city-hub__genome-row--skeleton"
                aria-hidden="true"
              >
                <span className="city-hub__genome-dot amok-skeleton amok-skeleton--circle" />
                <span className="city-hub__genome-skel-label amok-skeleton" />
                <span className="city-hub__genome-skel-value amok-skeleton" />
              </div>
            ))}
            <div className="city-hub__genome-divider" aria-hidden="true" />
            <div
              className="city-hub__genome-total city-hub__genome-total--skeleton"
              aria-hidden="true"
            >
              <span className="city-hub__genome-skel-label amok-skeleton" />
              <span className="city-hub__genome-skel-value amok-skeleton" />
            </div>
          </>
        ) : showEmpty ? (
          <EmptyState
            size="sm"
            title="No Spellbook Data Yet"
            description="This city's arts genome will show up here."
          />
        ) : (
          <>
            {items.map(item => (
              <div key={item.id || item.label} className="city-hub__genome-row">
                <span
                  className="city-hub__genome-dot"
                  style={{ background: item.color }}
                />
                <span className="city-hub__genome-label">{item.label}</span>
                <span className="city-hub__genome-value">{item.value}%</span>
              </div>
            ))}
            <div className="city-hub__genome-divider" aria-hidden="true" />
            <div className="city-hub__genome-total">
              <span>Total</span>
              <span>{Math.round(totalPercent)}%</span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export const CityHubAside = ({
  city = fallbackCity,
  recentPhotosRevision = 0,
}) => {
  const navigate = useNavigate()
  const currentUserId = useSelector(state => state.auth?.currentUser?.id)
  const cityShortName = city?.shortName || fallbackCity.shortName
  const cityId = city?.id || null
  const [collaborators, setCollaborators] = useState([])
  const [isLoading, setIsLoading] = useState(Boolean(cityId))
  const [recentPhotos, setRecentPhotos] = useState([])
  const [isPhotosLoading, setIsPhotosLoading] = useState(Boolean(cityId))

  useEffect(() => {
    let cancelled = false

    const loadMembers = async () => {
      if (!cityId) {
        setCollaborators([])
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      try {
        const response = await get(endpoints.getCityMembers(cityId), {
          params: {
            search: "",
            sort_by: "total_xp",
            sort_direction: "desc",
            page: 1,
            per_page: COLLABORATORS_WIDGET_LIMIT,
            "with[]": MEMBER_ROLE_WITH,
          },
        })
        if (cancelled) return
        if (!response?.data?.success) {
          setCollaborators([])
          return
        }

        const mapped = extractMembersPage(response)
          .map(user => mapCityMember(user, currentUserId))
          .filter(item => item.id)
          .slice(0, COLLABORATORS_WIDGET_LIMIT)

        setCollaborators(mapped)
      } catch {
        if (!cancelled) setCollaborators([])
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadMembers()
    return () => {
      cancelled = true
    }
  }, [cityId, currentUserId])

  useEffect(() => {
    const controller = new AbortController()

    const loadRecentPhotos = async () => {
      if (!cityId) {
        setRecentPhotos([])
        setIsPhotosLoading(false)
        return
      }

      setIsPhotosLoading(true)
      const result = await fetchRecentCityPhotos({
        cityId,
        limit: RECENT_PHOTOS_WIDGET_LIMIT,
        signal: controller.signal,
      })

      if (controller.signal.aborted || result?.aborted) return

      setRecentPhotos(result?.items || [])
      setIsPhotosLoading(false)
    }

    loadRecentPhotos()
    return () => {
      controller.abort()
    }
  }, [cityId, recentPhotosRevision])

  const openPhotographyTab = () => {
    if (!cityId) return
    navigate(cityHubTabPath("photography", cityId))
  }

  return (
    <aside className="city-hub__aside">
      <div className="city-hub__panel city-hub__panel--collaborators">
        <h3 className="city-hub__panel-title">
          Top Collaborators in {cityShortName}
        </h3>
        <div
          className="city-hub__collaborators"
          aria-busy={isLoading || undefined}
        >
          {isLoading ? <CollaboratorSkeletons /> : null}

          {!isLoading &&
            collaborators.map(person => (
              <div key={person.id} className="city-hub__collaborator">
                <Avatar
                  src={person.avatar}
                  name={person.name}
                  size="sm"
                  className="city-hub__collab-avatar"
                />
                <div className="city-hub__collab-info">
                  <div className="city-hub__collab-name">{person.name}</div>
                  <div className="city-hub__collab-role">{person.role}</div>
                </div>
                <span className="city-hub__collab-level">
                  <img
                    src={ICONS.cityHubLevelStar}
                    alt=""
                    width={10}
                    height={10}
                  />
                  Level {person.level}
                </span>
              </div>
            ))}

          {!isLoading && collaborators.length === 0 ? (
            <EmptyState
              size="sm"
              title="No Collaborators Yet"
              description="Members in this city will show up here."
            />
          ) : null}
        </div>
      </div>

      <div className="city-hub__panel city-hub__panel--photos">
        <h3 className="city-hub__panel-title">
          Recent Photos and Videos from {cityShortName}
        </h3>
        <div
          className={`city-hub__photos${
            !isPhotosLoading && recentPhotos.length === 0
              ? " city-hub__photos--empty"
              : ""
          }`}
          aria-busy={isPhotosLoading || undefined}
        >
          {isPhotosLoading ? <RecentPhotoSkeletons /> : null}

          {!isPhotosLoading &&
            recentPhotos.map(photo => (
              <button
                key={photo.id}
                type="button"
                className="city-hub__photo city-hub__photo--button"
                onClick={openPhotographyTab}
                aria-label={photo.title || "View photography"}
              >
                {photo.isVideo && !photo.thumbSrc && photo.src ? (
                  <video
                    className="city-hub__photo-media"
                    src={photo.src}
                    muted
                    playsInline
                    preload="metadata"
                  />
                ) : (
                  <MediaFallbackImage
                    src={photo.thumbSrc || photo.src}
                    fallback={ICONS.thumbPlaceholder}
                    alt=""
                  />
                )}
                {photo.isVideo ? (
                  <span className="city-hub__photo-play" aria-hidden="true">
                    <img src={photoPlayIcon} alt="" width={10} height={12} />
                  </span>
                ) : null}
              </button>
            ))}

          {!isPhotosLoading && recentPhotos.length === 0 ? (
            <EmptyState
              size="sm"
              className="city-hub__photos-empty"
              title="No Photos Yet"
              description={`Be the first to share photography from ${cityShortName}.`}
              actionText={cityId ? "View Photography" : undefined}
              onActionClick={cityId ? openPhotographyTab : undefined}
            />
          ) : null}
        </div>
      </div>
    </aside>
  )
}
